<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { CardTimezone } from "@/components/Cards";
import { Icon } from "@/components/Ui";
import moment from "moment";
import "moment-timezone";
import { cards } from "../../config/timezone";

const currentTimePST = ref({
  hours: "",
  minutes: "",
  seconds: ""
});
const currentTime = ref("");
const datePst = ref("");
const myDiffer = ref("");
const filterValue = ref("");
const inputRef = ref(null);
let selectedCard = reactive({
  timezone: "",
  pst: "",
});

const updateTimePST = () => {
  if (selectedCard.timezone) {
    currentTime.value = getCurrentTime(selectedCard.timezone);
  } else {
    currentTime.value = moment().format("HH:mm");
  }
  currentTimePST.value = {
    hours: moment()
      .tz("America/Los_Angeles")
      .format("HH "),
    minutes: moment()
      .tz("America/Los_Angeles")
      .format("mm"),
    seconds: moment()
      .tz("America/Los_Angeles")
      .format("ss")
  }
};

const updateDatePST = () => {
  datePst.value = moment()
    .tz("America/Los_Angeles")
    .format("dddd, DD MMMM YYYY");
};

const getCurrentTime = (timezone: string) => {
  return moment().tz(timezone).format("HH:mm");
};

const getCitiesTime = () => {
  cards.forEach((item, index) => {
    cards[index].time = getCurrentTime(item.timezone);
  });
};

const differTime = () => {
  if (selectedCard.pst) {
    myDiffer.value = selectedCard.pst;
  } else {
    const userDate = new Date();
    const pstDate = new Date().toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
    });
    const timeDiffInHours =
      (userDate.getTime() - new Date(pstDate).getTime()) / (1000 * 60 * 60);
    const sign = timeDiffInHours >= 0 ? "+" : "";
    myDiffer.value = sign + Math.round(timeDiffInHours);
  }
};

const filteredItems = computed(() => {
  return cards.filter((item) =>
    item.city.toLowerCase().includes(filterValue.value.toLowerCase())
  );
});

const selectCard = (card: { timezone: string; pst: string }) => {
  selectedCard = card;
};

const focusInput = () => {
  inputRef.value.focus();
};

onMounted(() => {
  updateTimePST();
  updateDatePST();
  getCitiesTime();
  differTime();
  setInterval(updateTimePST, 1000);
  setInterval(getCitiesTime, 100);
});
</script>

<template>
  <div :class="$style['time']">
    <h1 :class="$style['time-title']">
      {{ $t("timezone.pst") }}
      <span :class="$style['time-title-small']">{{ $t("timezone.now") }}</span>
    </h1>
    <div>
      <p :class="$style['time-time']">
        <span class="rtl:order-5">{{ currentTimePST.hours }}</span>
        <span class="rtl:order-4">&nbsp;:&nbsp;</span>
        <span class="rtl:order-3">{{ currentTimePST.minutes }}</span>
        <span class="rtl:order-2">&nbsp;:&nbsp;</span>
        <span class="rtl:order-1">{{ currentTimePST.seconds }}</span>
      </p>
    </div>
    <p :class="$style['time-date']">{{ datePst }}</p>
    <div :class="$style['time-text-wrapper']">
      <p :class="$style['time-text']">
        {{ $t("timezone.yourTime") }} ({{ currentTime }})
      </p>
      <div :class="$style.divider" />
      <p :class="$style['time-text']">
        {{ myDiffer }}:00 {{ $t("timezone.toPst") }}
      </p>
    </div>
    <div :class="$style['time-cards']">
      <div
        :class="$style['card']"
        @click="focusInput"
      >
        <div :class="$style['card-search']">
          <p :class="$style['card-city']">{{ $t("timezone.country") }}</p>
          <input
            ref="inputRef"
            v-model="filterValue"
            :class="$style['card-input']"
            :placeholder="$t('timezone.aboutPst')"
          />
        </div>
        <div :class="$style['card-icon']">
          <Icon :name="`search`" />
        </div>
      </div>
      <CardTimezone
        v-for="card in filteredItems"
        :key="card.id"
        :city="card.city"
        :time="card.time"
        :gmt="card.gmt"
        :pst="card.pst"
        @click="
          selectCard(card);
          differTime();
        "
      />
    </div>
  </div>
</template>

<style lang="scss" module>
.time {
  &-title {
    @apply font-semibold text-2xl xxs:text-3xl sm:text-4xl md:text-5xl text-center text-accent-primary;
    &-small {
      @apply font-extralight;
    }
  }
  &-date {
    @apply font-normal text-center text-greyscale-900 opacity-50 xxs:text-xl sm:text-3xl;
  }
  &-time {
    @apply font-black leading-loose xs:leading-tight tracking-widest whitespace-nowrap;
    @apply text-[12vw] xl:text-[180px] flex items-center justify-center text-neutral-800;
  }
  &-text {
    @apply font-light text-2xl xs:text-3xl sm:text-4xl text-center md:leading-[3rem];
    &-wrapper {
      @apply flex flex-col md:flex-row justify-center gap-[6px] md:gap-[33px];
      @apply pt-[22px] md:pt-[68px] pb-[28px] md:pb-[46px];
    }
  }
  &-cards {
    @apply flex items-center gap-2 flex-nowrap overflow-scroll pb-4;
  }
}
.card {
  @apply flex items-center h-[70px] p-[12px] flex-row gap-[28px];
  @apply rounded-12 border-[1px] border-solid border-gray-200 bg-gray-100 hover:cursor-text;
  &-search {
    @apply flex items-start justify-center flex-col;
  }
  &-city {
    @apply font-semibold text-[16px] leading-[19px] opacity-60 text-black;
  }
  &-input {
    @apply w-[155px] bg-gray-100;
    &:focus {
      @apply outline-none;
    }
  }
  &-icon {
    @apply flex items-center justify-center;
  }
}
.divider {
  @apply hidden md:block opacity-50 border-2 border-solid border-neutral-500;
}
</style>
